import { useMemo, useState } from 'react';
import * as THREE from 'three';
import { type ThreeEvent } from '@react-three/fiber';
import { useSketchStore } from '../../state/useSketchStore';
import { solidEffectiveVisible, vertexSelectionId, parseVertexSelectionId } from '../../core/sketch/types';
import { solidVertices, pointKeyOfVector } from '../../core/sketch/topology';
import { SKETCH_COLORS } from '../../themes/colors';
import { emitSketchDown, emitSketchMove, type SketchHit } from '../../tools/sketch/sketchToolBus';

/**
 * Sketch solid vertex pick layer — the pickable welded corners of solids.
 *
 * SketchUp makes endpoints first-class picks (hover a corner, click to
 * select, drag to move it and the attached faces). This layer mirrors the
 * solid-edge pick approach: each welded corner carries an invisible pick
 * sphere, so the pointer grabs the corner itself; the sphere radius sits
 * above the edge pick radius so at a corner the VERTEX wins the pointer
 * over the incident edges (SketchUp endpoint precedence). Hover and
 * selection render as overlay dots on top.
 */

const PICK_RADIUS = 0.07;
const DOT_RADIUS = 0.06;

function SolidVertexPick({
    solidId,
    vertexKey,
    position,
    onHover,
}: {
    solidId: string;
    vertexKey: string;
    position: THREE.Vector3;
    onHover: (vertexKey: string | null) => void;
}) {
    const hitOf = (e: ThreeEvent<PointerEvent>): SketchHit => ({
        kind: 'solidVertex',
        solidId,
        vertexKey,
        // The sphere surface can sit up to the pick radius off the true
        // corner; tools expect the hit ON the vertex.
        point: position.clone(),
        shiftKey: e.nativeEvent.shiftKey,
        ctrlKey: e.nativeEvent.ctrlKey,
        altKey: e.nativeEvent.altKey,
        button: e.button,
        pointerPx: { x: e.nativeEvent.clientX, y: e.nativeEvent.clientY },
    });

    return (
        <mesh
            position={position}
            visible={false}
            onPointerDown={(e: ThreeEvent<PointerEvent>) => {
                if (e.button !== 0) return;
                e.stopPropagation();
                emitSketchDown(hitOf(e));
            }}
            onPointerMove={(e: ThreeEvent<PointerEvent>) => {
                e.stopPropagation();
                emitSketchMove(hitOf(e));
            }}
            onPointerOver={(e: ThreeEvent<PointerEvent>) => {
                e.stopPropagation();
                onHover(vertexKey);
            }}
            onPointerOut={() => onHover(null)}
        >
            <sphereGeometry args={[PICK_RADIUS, 8, 8]} />
        </mesh>
    );
}

/** One solid's vertex picks + highlight dots, rebuilt only when the solid's
 *  loops change (the weld keys are derived from the contour points). */
function SolidVertexPicks({ solidId }: { solidId: string }) {
    const solid = useSketchStore((s) => s.solids[solidId]);
    const selectedIds = useSketchStore((s) => s.selectedIds);
    const setHovered = useSketchStore((s) => s.setHovered);
    const [hoveredKey, setHoveredKey] = useState<string | null>(null);

    const vertices = useMemo(() => {
        if (!solid) return [] as Array<{ key: string; position: THREE.Vector3 }>;
        return solidVertices(solid).map((position) => ({
            key: pointKeyOfVector(position),
            position,
        }));
    }, [solid]);

    const selectedKeys = useMemo(() => {
        const keys = new Set<string>();
        selectedIds.forEach((id) => {
            const parsed = parseVertexSelectionId(id);
            if (parsed && parsed.solidId === solidId) keys.add(parsed.vertexKey);
        });
        return keys;
    }, [selectedIds, solidId]);

    if (vertices.length === 0) return null;

    const handleHover = (vertexKey: string | null): void => {
        setHoveredKey(vertexKey);
        setHovered(vertexKey ? vertexSelectionId(solidId, vertexKey) : null);
    };

    return (
        <group>
            {vertices.map((v) => (
                <SolidVertexPick
                    key={v.key}
                    solidId={solidId}
                    vertexKey={v.key}
                    position={v.position}
                    onHover={handleHover}
                />
            ))}
            {/* Selected corners: filled dots above the ink lines. */}
            {[...selectedKeys].map((key) => {
                const v = vertices.find((entry) => entry.key === key);
                if (!v) return null;
                return (
                    <mesh key={key} position={v.position} raycast={() => null} renderOrder={7}>
                        <sphereGeometry args={[DOT_RADIUS, 12, 12]} />
                        <meshBasicMaterial color={SKETCH_COLORS.faceSelected} depthTest={false} transparent opacity={0.95} />
                    </mesh>
                );
            })}
            {/* Hovered corner: the SketchUp endpoint read. */}
            {hoveredKey && (() => {
                const v = vertices.find((entry) => entry.key === hoveredKey);
                if (!v) return null;
                return (
                    <mesh position={v.position} raycast={() => null} renderOrder={7}>
                        <sphereGeometry args={[DOT_RADIUS, 12, 12]} />
                        <meshBasicMaterial color={SKETCH_COLORS.primary} depthTest={false} transparent opacity={0.95} />
                    </mesh>
                );
            })()}
        </group>
    );
}

export function SketchSolidVertexPickLayer() {
    const solids = useSketchStore((s) => s.solids);
    const tags = useSketchStore((s) => s.tags);

    // Vertex picks stay meaningful in every display style, but hidden or
    // locked solids offer nothing to edit.
    const pickableIds = useMemo(
        () => Object.values(solids)
            .filter((solid) => solidEffectiveVisible(solid, tags) && !solid.locked)
            .map((solid) => solid.id),
        [solids, tags]
    );

    return (
        <group>
            {pickableIds.map((id) => (
                <SolidVertexPicks key={id} solidId={id} />
            ))}
        </group>
    );
}
